/**
 * Per-document coordination primitives for the PostgreSQL language service.
 *
 * This module owns the MECHANISM of keeping one editor buffer in step with the
 * pgls daemon — ordering, de-duplication, rate limiting and the
 * what-the-editor-currently-holds bookkeeping. It deliberately knows nothing
 * about LSP, IPC, `DocumentState` or connections: every entry point takes a
 * document URI and a callback. The POLICY — which notification to send, when a
 * reconnect is warranted, what counts as delivered — stays in
 * `pgLanguageServiceSync.ts`.
 *
 * Each primitive exists because of a specific defect; the comments say which,
 * so a future reader does not "simplify" one away. Keyed by document URI, and
 * `clearDocumentSyncState` drops every key together when a document closes.
 */

/** Per-URI op chain. Values are never-rejecting handles (see `enqueueDocumentOp`). */
const queues = new Map<string, Promise<boolean>>();

/** Per-URI text the editor last asked for (see `setDesiredText`). */
const desired = new Map<string, string>();

/** Per-URI in-flight recovery attempt (see `runRecoveryOnce`). */
const recoveriesInFlight = new Map<string, Promise<boolean>>();

/** Per-URI earliest time a new recovery may start (see `runRecoveryOnce`). */
const recoveryCooldownUntil = new Map<string, number>();

/**
 * How long to wait after a FAILED recovery before trying again. Without it a
 * daemon that is down for good (a pgls panic that reproduces on this buffer)
 * turns every 250 ms typing pause into a dispose + respawn + 500 ms handshake,
 * plus a status-pill flip — a crash loop paced by the user's typing.
 */
export const RECONNECT_COOLDOWN_MS = 5000;

/**
 * Serialize one document's server traffic on the per-URI queue.
 *
 * `didOpen` and `didChange` MUST NOT interleave. `didOpen` reads the buffer
 * and then awaits the notification, while the document is not yet marked open
 * — so an update landing inside that window takes the local-only branch,
 * records the new text as sent, and returns. The already-in-flight `didOpen`
 * then carries the OLD text, and every later update is skipped as "text hasn't
 * changed": the server keeps a buffer the user never had (usually the empty
 * one a fresh tab registers with) and publishes diagnostics for it forever,
 * with no edit able to recover it short of changing the text twice.
 *
 * Returns the op's own result, so callers still see a failure; the value
 * stored in the map is a separate never-rejecting handle so one failed op
 * cannot poison the chain for the next one.
 */
export const enqueueDocumentOp = (
  uri: string,
  op: () => Promise<boolean>
): Promise<boolean> => {
  const previousQueue = queues.get(uri) ?? Promise.resolve(true);
  const nextQueue = previousQueue.catch(() => true).then(op);
  const tracked = nextQueue.then(
    () => true,
    () => true,
  );
  queues.set(uri, tracked);
  tracked.finally(() => {
    if (queues.get(uri) === tracked) {
      queues.delete(uri);
    }
  });
  return nextQueue;
};

/**
 * Record the text the EDITOR currently holds.
 *
 * Not the same thing as the text the server last accepted. The two diverge the
 * moment a send fails, and every recovery path must resync to THIS one:
 * re-opening with the last accepted text would restore a buffer the user has
 * already moved past, and a retry that resends its own captured text would
 * overwrite a newer edit that landed while it was failing.
 */
export const setDesiredText = (uri: string, text: string): void => {
  desired.set(uri, text);
};

/** The editor's current buffer for `uri`, or undefined if none was recorded. */
export const getDesiredText = (uri: string): string | undefined => desired.get(uri);

/**
 * Run a recovery attempt for `uri`: at most one at a time, and not again until
 * `RECONNECT_COOLDOWN_MS` has passed after a failure.
 *
 * Both guards became load-bearing once editing (not just a user-initiated
 * hover) could drive recovery. Hover and signature help ask rarely; typing
 * fires every 250 ms, so an unguarded attempt would race itself into duplicate
 * language-server processes — the main process's `ensureInstance` has no lock
 * of its own, and two overlapping attempts spawn two children, orphaning
 * whichever loses the `instances.set` race — and would spin forever on a
 * daemon that is never coming back.
 *
 * A rejection counts as a failure and is absorbed: callers get `false`.
 */
export const runRecoveryOnce = (
  uri: string,
  attempt: () => Promise<boolean>
): Promise<boolean> => {
  const inFlight = recoveriesInFlight.get(uri);
  if (inFlight) return inFlight;

  const cooldownUntil = recoveryCooldownUntil.get(uri) ?? 0;
  if (Date.now() < cooldownUntil) return Promise.resolve(false);

  const running = attempt()
    .then((ok) => {
      if (ok) recoveryCooldownUntil.delete(uri);
      else recoveryCooldownUntil.set(uri, Date.now() + RECONNECT_COOLDOWN_MS);
      return ok;
    })
    .catch(() => {
      recoveryCooldownUntil.set(uri, Date.now() + RECONNECT_COOLDOWN_MS);
      return false;
    });

  recoveriesInFlight.set(uri, running);
  running.finally(() => {
    if (recoveriesInFlight.get(uri) === running) recoveriesInFlight.delete(uri);
  });
  return running;
};

/**
 * Forget everything this module holds for `uri`.
 *
 * Called when a document closes, so a reconnect that was already in flight
 * cannot re-open a closed tab's buffer and the maps do not grow for the life
 * of the session.
 */
export const clearDocumentSyncState = (uri: string): void => {
  queues.delete(uri);
  desired.delete(uri);
  recoveriesInFlight.delete(uri);
  recoveryCooldownUntil.delete(uri);
};
